import React, { useState, useEffect, useCallback } from 'react';
import { getAllAdminTransactions } from '../../utils/api';
import Pagination from '../Pagination';

const PER_PAGE = 20;

// Clean, sorted view of EVERY transaction across all users (newest first).
// Each row shows the user's referral code + their upline's referral code, and an
// admin-only link to the on-chain payment on the block explorer (BscScan).
const RecentActivity = ({ config, f: fProp, BC: BCProp, SYM: SYMProp, fBC, vBC: vBCProp, showUsdt, setShowUsdt, isNotUsdt }) => {
  const f = fProp || ((n, d = 4) => (n == null || isNaN(n)) ? '0' : parseFloat(n).toFixed(d));
  const BC = BCProp || config?.baseCurrency || 'USDT';
  const SYM = SYMProp || config?.tokenSymbol || 'ROBO';
  const vBC = vBCProp || ((usdt, d = 4) => `${fBC ? fBC(usdt, d) : f(usdt, d)} ${BC}`);
  const tr = (a) => a ? `${a.slice(0, 6)}...${a.slice(-4)}` : '—';

  const [rows, setRows] = useState([]);
  const [explorerUrl, setExplorerUrl] = useState(config?.networkExplorer || 'https://bscscan.com');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [typeFilter, setTypeFilter] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAllAdminTransactions(page, PER_PAGE, typeFilter, search);
      setRows(data.transactions || []);
      setPages(data.pagination?.pages || 1);
      setTotal(data.pagination?.total || 0);
      if (data.explorerUrl) setExplorerUrl(data.explorerUrl.replace(/\/+$/, ''));
    } catch (e) { console.error('all-transactions load error:', e); }
    setLoading(false);
  }, [page, typeFilter, search]);

  useEffect(() => { load(); }, [load]);

  const doSearch = () => { setPage(1); setSearch(searchInput.trim()); };
  const clearSearch = () => { setSearchInput(''); setPage(1); setSearch(''); };
  const onType = (val) => { setPage(1); setTypeFilter(val); };

  const typeLabel = (type) => ({
    buy: 'Buy', sell: 'Sell', level_income_claim: 'Claim',
    p2p_transfer: 'P2P Sent', p2p_receive: 'P2P Received',
  }[type] || type);

  const typeClass = (type) => {
    if (type === 'buy' || type === 'p2p_receive') return 'buy';
    if (type === 'sell') return 'sell';
    if (type === 'p2p_transfer') return 'p2p';
    return 'claim';
  };

  const fmtDate = (d) => new Date(d).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
  const fmtTime = (d) => new Date(d).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });

  return (
    <div className="atx-root fade-in">
      <style>{`
        .atx-root .atx-date { line-height: 1.35; }
        .atx-root .atx-date-d { display: block; white-space: nowrap; }
        .atx-root .atx-date-t { display: block; font-size: 11px; color: var(--text-3); white-space: nowrap; }
        .atx-root .atx-code { font-weight: 700; }
        .atx-root .atx-sub { display: block; font-size: 11px; color: var(--text-3); white-space: nowrap; }
        .atx-root .atx-toolbar { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; margin-bottom: 16px; }
        .atx-root .atx-toolbar .input { min-width: 220px; }
        .atx-root .atx-view-link { white-space: nowrap; }
        @media (max-width: 768px) {
          .atx-root .table th,
          .atx-root .table td { padding: 8px 6px; font-size: 12px; }
          .atx-root .table .mono { font-size: 11px; }
          .atx-root .atx-date-d { font-size: 12px; }
          .atx-root .atx-date-t, .atx-root .atx-sub { font-size: 10px; }
          .atx-root .atx-toolbar .input { min-width: 0; flex: 1 1 100%; }
        }
      `}</style>

      <h2 className="adm-title">All Transactions</h2>
      <p className="adm-hint">Every buy, sell, claim and P2P transfer across all users — newest first. Search by ROBO ID, referral code, wallet or tx hash.</p>

      <div className="atx-toolbar">
        <select className="input" value={typeFilter} onChange={e => onType(e.target.value)} style={{ maxWidth: 180 }}>
          <option value="">All types</option>
          <option value="buy">Buy</option>
          <option value="sell">Sell</option>
          <option value="level_income_claim">Claim</option>
          <option value="p2p_transfer">P2P Sent</option>
          <option value="p2p_receive">P2P Received</option>
        </select>
        <input className="input" placeholder="Search ROBO ID, referral code, wallet, tx hash..." value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') doSearch(); }} />
        <button className="btn btn-primary" onClick={doSearch}>Search</button>
        {search && <button className="btn btn-outline btn-sm" onClick={clearSearch}>Clear</button>}
        {isNotUsdt && setShowUsdt && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto', fontSize: 13, cursor: 'pointer' }}>
            <input type="checkbox" checked={!!showUsdt} onChange={e => setShowUsdt(e.target.checked)} />
            Show USDT
          </label>
        )}
      </div>

      {loading ? (
        <div className="page-loading"><div className="spinner" /></div>
      ) : rows.length > 0 ? (
        <>
        <div className="table-wrap"><table className="table">
          <thead><tr>
            <th>Date</th><th>Type</th><th>User</th><th>Upline</th>
            <th>Details</th><th>Tokens</th><th>Value</th><th>Price</th><th>On-chain</th>
          </tr></thead>
          <tbody>{rows.map(tx => (
            <tr key={tx._id}>
              <td className="atx-date">
                <span className="atx-date-d">{fmtDate(tx.createdAt)}</span>
                <span className="atx-date-t">{fmtTime(tx.createdAt)}</span>
              </td>
              <td><span className={`d-tx-badge ${typeClass(tx.type)}`}>{typeLabel(tx.type)}</span></td>
              <td>
                <span className="atx-code mono">{tx.referralCode || '—'}</span>
                <span className="atx-sub mono">{tx.roboId || tr(tx.walletAddress)}</span>
              </td>
              <td>
                <span className="atx-code mono">{tx.uplineReferralCode || '—'}</span>
                {tx.uplineRoboId && <span className="atx-sub mono">{tx.uplineRoboId}</span>}
              </td>
              <td className="mono" style={{ fontSize: 12 }}>
                {tx.type === 'buy' && `Paid ${f(tx.paymentAmount || tx.bnbAmount, 4)} ${tx.paymentCurrency || BC}`}
                {tx.type === 'sell' && (
                  <>
                    Received {fBC ? fBC(tx.bnbReceived, 4) : f(tx.bnbReceived, 4)} {BC}
                    {tx.tokensBurned > 0 && <span className="text-3"> ({f(tx.tokensBurned, 2)} burned)</span>}
                  </>
                )}
                {tx.type === 'level_income_claim' && `Claimed ${fBC ? fBC(tx.claimAmount, 4) : f(tx.claimAmount, 4)} ${BC}`}
                {tx.type === 'p2p_transfer' && (
                  <>To: <strong>{tx.p2pReceiverRoboId || tr(tx.p2pReceiverWallet)}</strong></>
                )}
                {tx.type === 'p2p_receive' && (
                  <>From: <strong>{tx.p2pReceiverRoboId || tr(tx.p2pReceiverWallet)}</strong></>
                )}
              </td>
              <td className="mono">
                {tx.type === 'buy' && <span className="text-green">+{f(tx.tokensToUser, 2)}</span>}
                {tx.type === 'sell' && <span className="text-red">-{f(tx.tokensSold, 2)}</span>}
                {tx.type === 'level_income_claim' && '—'}
                {tx.type === 'p2p_transfer' && <span className="text-red">-{f(tx.tokensSold, 2)}</span>}
                {tx.type === 'p2p_receive' && <span className="text-green">+{f(tx.tokensToUser, 2)}</span>}
              </td>
              <td className="mono">
                {tx.type === 'buy' && vBC(tx.bnbAmount, 4)}
                {tx.type === 'sell' && vBC(tx.bnbReceived, 4)}
                {tx.type === 'level_income_claim' && vBC(tx.claimAmount, 4)}
                {tx.type === 'p2p_transfer' && vBC((tx.tokensSold || 0) * (tx.tokenPrice || 0), 4)}
                {tx.type === 'p2p_receive' && vBC((tx.tokensToUser || 0) * (tx.tokenPrice || 0), 4)}
              </td>
              <td className="mono">{f(tx.tokenPrice, 6)}</td>
              <td>
                {tx.txHash ? (
                  <a className="btn btn-outline btn-sm atx-view-link" href={`${explorerUrl}/tx/${tx.txHash}`} target="_blank" rel="noreferrer">View ↗</a>
                ) : <span className="text-3">—</span>}
              </td>
            </tr>
          ))}</tbody>
        </table></div>
        <Pagination page={page} pages={pages} total={total} perPage={PER_PAGE} onPage={setPage} />
        </>
      ) : <div className="d-empty">No transactions found.</div>}
    </div>
  );
};

export default RecentActivity;
